import React, { MouseEvent, useMemo, useState } from "react";
import clsx from "clsx";
import { Badge, Menu, MenuItem, Tab, Tabs, TabsProps, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useLocation, useNavigate } from "react-router-dom";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import { ICart, ITabItem } from "@/shared/types";
import { useCommonActions } from "@/entities/common";
import { ROUTER_PATHS } from "@/shared/config/router-paths";
import { getCartItemsCount } from "@/shared/utils/cartHelpers";
import useStyles from "./styles";

interface CustomTabsMUIProps extends TabsProps {
  cart: ICart | null;
  tabs: Array<ITabItem>;
  activeTab: ITabItem | null;
  isClientAreaNav: boolean;
  cssVariant: "navBarRegular" | "navBar";
}

const InventoryLabelWrapper = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 42px;
  min-width: 0;
  overflow: hidden;
`;

const InventoryTabContent = styled.span`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 42px;
  min-width: 0;
`;

const InventoryLabelBase = styled.span<{ $isExpanded: boolean }>`
  display: inline-flex;
  max-width: 100%;
  font-size: ${({ $isExpanded }) => ($isExpanded ? "1.18rem" : "1.8rem")};
  line-height: ${({ $isExpanded }) => ($isExpanded ? "1.45rem" : "2.2rem")};
  font-weight: ${({ $isExpanded }) => ($isExpanded ? 600 : 400)};
  letter-spacing: ${({ $isExpanded }) => ($isExpanded ? "0.08em" : "0.01em")};
  text-transform: ${({ $isExpanded }) => ($isExpanded ? "uppercase" : "none")};
  transition:
    font-size 420ms cubic-bezier(0.2, 0.82, 0.22, 1),
    line-height 420ms cubic-bezier(0.2, 0.82, 0.22, 1),
    letter-spacing 420ms cubic-bezier(0.2, 0.82, 0.22, 1),
    opacity 280ms ease;
  white-space: nowrap;
`;

const InventoryLabelSuffix = styled.span<{ $isExpanded: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-width: 0;
  opacity: ${({ $isExpanded }) => ($isExpanded ? 1 : 0)};
  max-height: ${({ $isExpanded }) => ($isExpanded ? "18px" : "0")};
  margin-top: ${({ $isExpanded }) => ($isExpanded ? "2px" : "0")};
  transform: translateY(${({ $isExpanded }) => ($isExpanded ? "0" : "-4px")});
  transform-origin: center center;
  transition:
    opacity 320ms ease,
    transform 520ms cubic-bezier(0.2, 0.82, 0.22, 1),
    max-height 320ms ease,
    margin-top 320ms ease;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: center;
  font-size: 1.42rem;
  line-height: 1.82rem;
  font-weight: 500;
`;

const InventoryMenuItemContent = styled.span`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  min-width: 0;
`;

const InventoryMenuItemText = styled.span`
  display: inline-flex;
  align-items: center;
  min-width: 0;
  font-size: 1.4rem;
  line-height: 2rem;
  font-weight: 500;
`;

const InventoryMenuItemBadge = styled.span<{ $isCurrent?: boolean; $isDisabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: ${({ $isCurrent }) => ($isCurrent ? "72px" : "96px")};
  padding: ${({ $isCurrent }) => ($isCurrent ? "4px 10px" : "4px 8px")};
  border-radius: 999px;
  font-size: 1.1rem;
  line-height: 1.4rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  white-space: nowrap;
  background: ${({ $isCurrent, $isDisabled }) =>
    $isCurrent
      ? "rgba(17, 208, 255, 0.14)"
      : $isDisabled
        ? "rgba(255, 255, 255, 0.05)"
        : "transparent"};
  color: ${({ $isCurrent, $isDisabled }) =>
    $isCurrent ? "#11d0ff" : $isDisabled ? "rgba(255, 255, 255, 0.38)" : "transparent"};
  border: 1px solid
    ${({ $isCurrent, $isDisabled }) =>
      $isCurrent
        ? "rgba(17, 208, 255, 0.26)"
        : $isDisabled
          ? "rgba(255, 255, 255, 0.06)"
          : "transparent"};
`;

const INVENTORY_ROUTE_TITLES: Array<{ path: string; label: string }> = [
  { path: ROUTER_PATHS.clientArea.specimenSearch.path, label: "Clinical Specimens" },
  { path: ROUTER_PATHS.clientArea.infectiousDonors.path, label: "Donor Specimens" },
  { path: ROUTER_PATHS.clientArea.tissueBlocks.path, label: "Tissue Blocks" },
];

const CLIENT_INVENTORY_ITEMS: Array<{ label: string; path?: string }> = [
  { label: "Clinical Specimens", path: ROUTER_PATHS.clientArea.specimenSearch.path },
  { label: "Donor Specimens", path: ROUTER_PATHS.clientArea.infectiousDonors.path },
  { label: "Tissue Blocks" },
];

const BACK_OFFICE_INVENTORY_ROUTE_TITLES: Array<{ path: string; label: string }> = [
  { path: ROUTER_PATHS.backOffice.bloodClinical.path, label: "Clinical Specimens" },
  { path: ROUTER_PATHS.backOffice.biomarkers.path, label: "Clinical Specimens" },
  { path: ROUTER_PATHS.backOffice.infectiousDonors.path, label: "Donor Specimens" },
];

const BACK_OFFICE_INVENTORY_ROUTES = [
  ROUTER_PATHS.backOffice.inventory.path,
  ROUTER_PATHS.backOffice.bloodClinical.path,
  ROUTER_PATHS.backOffice.biomarkers.path,
  ROUTER_PATHS.backOffice.infectiousDonors.path,
];

const BACK_OFFICE_INVENTORY_ITEMS: Array<{ label: string; path?: string }> = [
  { label: "Clinical Specimens", path: ROUTER_PATHS.backOffice.bloodClinical.path },
  { label: "Donor Specimens", path: ROUTER_PATHS.backOffice.infectiousDonors.path },
  { label: "Tissue Blocks" },
];

const NavTabs: React.FC<CustomTabsMUIProps> = ({
       cart,
       tabs,
       activeTab,
       isClientAreaNav,
       cssVariant = "navBarRegular",
     }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { navigationTabClicked } = useCommonActions();
  const navigate = useNavigate();
  const location = useLocation();
  const [showCartTooltip, setShowCartTooltip] = useState(false);
  const [inventoryMenuAnchor, setInventoryMenuAnchor] = useState<HTMLElement | null>(null);
  const inventoryMenuItems = useMemo(
    () => (isClientAreaNav ? CLIENT_INVENTORY_ITEMS : BACK_OFFICE_INVENTORY_ITEMS),
    [isClientAreaNav]
  );

  const inventoryContextLabel = useMemo(() => {
    if (isClientAreaNav) {
      const matchedRoute = INVENTORY_ROUTE_TITLES.find(({ path }) =>
        location.pathname.startsWith(path)
      );

      return matchedRoute?.label ?? "";
    }

    const matchedRoute = BACK_OFFICE_INVENTORY_ROUTE_TITLES.find(({ path }) =>
      location.pathname.startsWith(path)
    );

    return matchedRoute?.label ?? "";
  }, [isClientAreaNav, location.pathname]);

  const selectedTabValue = useMemo(() => {
    if (location.pathname === ROUTER_PATHS.clientArea.profile.path) {
      return false;
    }

    const inventoryTab = tabs.find((tab) => tab.name === "Inventory");
    const isInventoryRoute =
      (isClientAreaNav &&
        (
        location.pathname === ROUTER_PATHS.clientArea.inventory.path ||
        INVENTORY_ROUTE_TITLES.some(({ path }) => location.pathname.startsWith(path))
        )) ||
      (!isClientAreaNav &&
        BACK_OFFICE_INVENTORY_ROUTES.some((path) => location.pathname.startsWith(path)));

    if (inventoryTab && isInventoryRoute) {
      return inventoryTab.id;
    }

    const matchedTab = tabs.find((tab) => tab.path === location.pathname);

    return matchedTab?.id ?? activeTab?.id ?? false;
  }, [activeTab?.id, isClientAreaNav, location.pathname, tabs]);

  const handleCloseInventoryMenu = () => {
    setInventoryMenuAnchor(null);
  };

  const handleInventoryItemClick = (path?: string) => {
    handleCloseInventoryMenu();

    if (!path) {
      return;
    }

    navigate(path);
  };

  const isActiveInventoryMenuItem = (path?: string) => {
    if (!path) {
      return false;
    }

    return location.pathname.startsWith(path);
  };

  const handleTabClick = (
    event: MouseEvent<HTMLElement>,
    selectedTab: ITabItem,
    index: number
  ) => {
    // Check if Cart tab is clicked and cart is empty
    if (selectedTab.name === "Cart" && getCartItemsCount(cart) === 0) {
      setShowCartTooltip(true);
      setTimeout(() => setShowCartTooltip(false), 2000);
      return;
    }

    navigationTabClicked({ value: index });

    if (selectedTab.name === "Inventory" && inventoryContextLabel) {
      setInventoryMenuAnchor(event.currentTarget);
      return;
    }

    navigate(selectedTab.path);
  };

  const className = clsx(classes.root, cssVariant && classes[cssVariant]);
  const getIconTabByName = (name: string): JSX.Element | undefined => {
    switch (name) {
      case "Cart":
        return <ShoppingCartOutlinedIcon />;
      default:
        return undefined;
    }
  };

  const badgeCount = getCartItemsCount(cart);

  return (
    <>
      <Tabs
        value={selectedTabValue}
        className={className}
        role="navigation"
      >
        {tabs.map((tab: ITabItem, index: number) => {
          const isInventoryTab = tab.name === "Inventory";
          const label = isInventoryTab ? (
            <InventoryTabContent>
              <InventoryLabelWrapper>
                <InventoryLabelBase $isExpanded={Boolean(inventoryContextLabel)}>
                  {t(tab.translateKey)}
                </InventoryLabelBase>
              </InventoryLabelWrapper>
              <InventoryLabelSuffix $isExpanded={Boolean(inventoryContextLabel)}>
                {inventoryContextLabel}
              </InventoryLabelSuffix>
            </InventoryTabContent>
          ) : tab.name === "Cart" ? (
            <Badge badgeContent={badgeCount}>{t(tab.translateKey)}</Badge>
          ) : (
            t(tab.translateKey)
          );

          const tabElement = (
            <Tab
              icon={isInventoryTab ? undefined : getIconTabByName(tab.name)}
              key={tab.id}
              label={label}
              onClick={(event) => handleTabClick(event, tab, index)}
              className={clsx(classes.tab, {
                [classes.cartTab]: tab.name === 'Cart',
                [classes.hidden]: !tab.render,
              })}
              sx={
                tab.name === "Cart"
                  ? { position: "absolute !important" }
                  : isInventoryTab
                    ? {
                        width: 312,
                        minWidth: 312,
                        justifyContent: "flex-start",
                      }
                    : {}
              }
            />
          );

          if (tab.name === "Cart") {
            return (
              <Tooltip
                key={tab.id}
                title={t("your_cart_is_empty")}
                open={showCartTooltip}
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      fontSize: '14px',
                    }
                  }
                }}
              >
                {tabElement}
              </Tooltip>
            );
          }

          return tabElement;
        })}
      </Tabs>
      <Menu
        anchorEl={inventoryMenuAnchor}
        open={Boolean(inventoryMenuAnchor)}
        onClose={handleCloseInventoryMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        PaperProps={{
          sx: (theme) => ({
            mt: 1.25,
            minWidth: 280,
            padding: theme.spacing(1),
            borderRadius: "18px",
            background:
              "linear-gradient(180deg, rgba(13, 35, 79, 0.98) 0%, rgba(10, 27, 64, 0.98) 100%)",
            border: "1px solid rgba(255, 255, 255, 0.08)",
            boxShadow: "0 24px 56px rgba(7, 18, 43, 0.34)",
            backdropFilter: "blur(10px)",
            overflow: "hidden",
          }),
        }}
        MenuListProps={{ sx: { padding: 0 } }}
      >
        {inventoryMenuItems.map((item) => (
          <MenuItem
            key={item.label}
            selected={isActiveInventoryMenuItem(item.path)}
            disabled={!item.path}
            onClick={() => handleInventoryItemClick(item.path)}
            sx={{
              minHeight: 48,
              px: 1.5,
              py: 1.25,
              borderRadius: "12px",
              color: "#ffffff",
              transition: "background-color 160ms ease, transform 160ms ease",
              "& + &": {
                mt: 0.5,
              },
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.08)",
              },
              "&.Mui-selected": {
                backgroundColor: "rgba(17, 208, 255, 0.12)",
              },
              "&.Mui-selected:hover": {
                backgroundColor: "rgba(17, 208, 255, 0.16)",
              },
              "&.Mui-disabled": {
                opacity: 1,
                color: "rgba(255, 255, 255, 0.44)",
              },
            }}
          >
            <InventoryMenuItemContent>
              <InventoryMenuItemText>{item.label}</InventoryMenuItemText>
              <InventoryMenuItemBadge
                $isCurrent={isActiveInventoryMenuItem(item.path)}
                $isDisabled={!item.path}
              >
                {isActiveInventoryMenuItem(item.path)
                  ? "Current"
                  : !item.path
                    ? "Coming Soon"
                    : ""}
              </InventoryMenuItemBadge>
            </InventoryMenuItemContent>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default NavTabs;
